<script setup lang="ts">
import type { VueNode } from 'ant-design-vue/es/_util/type';
import type { AxiosError } from 'axios';

import type { PostSubmit, PreSubmit } from '~/lib/fw/types/form';
import { SaveOutlined } from '@ant-design/icons-vue';
import { notification } from 'ant-design-vue';
import { cloneDeep } from 'lodash-es';
import { onUnmounted, reactive, ref, toRaw } from 'vue';
import { useConfig } from '~/lib/fw/useFw';
import { poll, post, toFormValues } from '~/lib/fw/utils';

interface SubmitJobResult {
  jobId: string;
}

interface Props {
  action: string;
  initialModel?: object;
  preSubmit?: PreSubmit;
  postSubmit?: PostSubmit;
  waitForResult?: boolean;
  errorType?: 'notification' | 'none';
  errorNotificationDefaultMessage?: string | VueNode | (() => VueNode);
  successType?: 'notification' | 'result' | 'none';
  successNotificationMessage?: string | VueNode | (() => VueNode);
  submitButtonDisabled?: boolean;
  submitButtonBlock?: boolean;
  submitButtonText?: string;
  submitButtonIconVisible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  initialModel: () => ({}),
  waitForResult: true,
  submitButtonDisabled: false,
  submitButtonBlock: false,
  submitButtonText: 'Salvar',
  submitButtonIconVisible: true,
  showSubmitSucessResult: true,
  errorType: 'notification',
  errorNotificationDefaultMessage: 'Tivemos um erro inesperado',
  successType: 'result',
  successNotificationMessage: 'Registro realizado com sucesso',
});

const emit = defineEmits(['success', 'error', 'close']);

const fwConfig = useConfig();
const jobId = ref<string | undefined>(undefined);
// eslint-disable-next-line ts/no-unsafe-function-type
const cancelPollJobFunction = ref<Function | undefined>(undefined);
const commandData = ref(null);
const formRef = ref<any>();
const submitStatusRef = ref<'loading' | 'success' | 'error' | null>(null);
const originalModel: any = cloneDeep(toRaw(props.initialModel));
const model = reactive<any>(props.initialModel ?? {});
let resultModel: any = {};

function getErrorMessage(pError: AxiosError): string | null | undefined {
  if (pError.status === 400) {
    return (pError.response?.data as any)?.errorMessage;
  } else {
    return null;
  }
}

async function submit() {
  await formRef.value.validate();

  submitStatusRef.value = 'loading';

  resultModel = cloneDeep(toRaw(model));

  let requestData: any = toFormValues(resultModel, originalModel);

  if (props.preSubmit) {
    const preSubmitResult = await props.preSubmit(requestData);

    if (!preSubmitResult.continue) {
      submitStatusRef.value = null;
      return;
    }

    requestData = preSubmitResult.data;
  }

  const submitJobResult = await post<SubmitJobResult>(
    `${fwConfig.apiUrl}/${props.action}`,
    requestData,
  );

  if (!submitJobResult.ok) {
    const errorMessage =
      getErrorMessage(submitJobResult.error as AxiosError) ??
      props.errorNotificationDefaultMessage!;

    if (props.errorType === 'notification') {
      notification.error({
        message: errorMessage,
        duration: 10,
      });
    }

    emit('error', { message: errorMessage });

    submitStatusRef.value = 'error';
    return;
  }

  if (!props.waitForResult) {
    if (props.successType === 'notification') {
      notification.success({
        message: props.successNotificationMessage!,
        duration: 10,
      });
    }

    emit('success', { jobId: submitJobResult.value.jobId });

    submitStatusRef.value = 'success';
    return;
  }

  jobId.value = submitJobResult.value.jobId;

  if (!jobId.value) {
    if (props.successType === 'notification') {
      notification.success({
        message: props.successNotificationMessage!,
        duration: 10,
      });
    }

    emit('success', { jobId: undefined });

    submitStatusRef.value = 'success';
    return;
  }

  try {
    const pollResult = poll(`${fwConfig.apiUrl}/jobs/${jobId.value}`, {
      delay: 3000,
      maxAttempts: 20,
    });
    cancelPollJobFunction.value = pollResult.cancel;
    const response = await pollResult.promise;

    commandData.value = response;

    // FAILED JOB
    if (response.status === 4) {
      if (props.errorType === 'notification') {
        notification.error({
          message: props.errorNotificationDefaultMessage!,
          duration: 10,
        });
      }

      emit('error', {
        message: props.errorNotificationDefaultMessage!,
        jobId: jobId.value,
      });

      submitStatusRef.value = 'error';
      return;
    }

    const results = response.results;

    if (results?.length === 1 && results[0].isSuccess) {
      if (props.successType === 'notification') {
        notification.success({
          message: props.successNotificationMessage!,
          duration: 10,
        });
      }

      emit('success', {
        message: props.successNotificationMessage!,
        jobId: jobId.value,
      });

      submitStatusRef.value = 'success';
      return;
    }

    if (results?.length === 1 && !results[0].isSuccess) {
      const jobData = results[0];
      const errorMessage =
        jobData.errors[0] || props.errorNotificationDefaultMessage!;
      if (props.errorType === 'notification') {
        notification.error({
          message: errorMessage,
          duration: 10,
        });
      }

      emit('error', { message: errorMessage, jobId: jobId.value });

      submitStatusRef.value = 'error';
      return;
    }

    if (typeof results === 'object' && !results.isSuccess) {
      const errorMessage =
        results.errors[0] || props.errorNotificationDefaultMessage!;

      if (props.errorType === 'notification') {
        notification.error({
          message: errorMessage,
          duration: 10,
        });
      }

      emit('error', { message: errorMessage, jobId: jobId.value });

      submitStatusRef.value = 'error';
      return;
    }

    // results > 1

    if (props.successType === 'notification') {
      notification.success({
        message: props.successNotificationMessage!,
        duration: 10,
      });
    }

    emit('success', { jobId: jobId.value, data: results });

    submitStatusRef.value = 'success';
  } catch {
    if (props.errorType === 'notification') {
      notification.error({
        message: props.errorNotificationDefaultMessage!,
        duration: 10,
      });
    }

    emit('error', {
      message: props.errorNotificationDefaultMessage!,
      jobId: jobId.value,
    });

    submitStatusRef.value = 'error';
  }
}

onUnmounted(() => {
  if (cancelPollJobFunction.value) {
    cancelPollJobFunction.value();
  }
});

defineExpose({
  model,
});
</script>

<template>
  <a-form
    v-if="props.successType !== 'result' || submitStatusRef !== 'success'"
    ref="formRef"
    :model="model"
    name="basic"
    autocomplete="off"
    layout="vertical"
  >
    <slot name="form" :model="model" />

    <a-form-item>
      <slot
        name="submitButton"
        :command-status="submitStatusRef"
        :submit="submit"
      >
        <a-button
          :block="props.submitButtonBlock"
          type="primary"
          :loading="submitStatusRef === 'loading'"
          size="large"
          :disabled="props.submitButtonDisabled"
          @click.prevent="submit"
        >
          <template v-if="props.submitButtonIconVisible" #icon>
            <slot name="submitButtonIcon">
              <SaveOutlined />
            </slot>
          </template>
          {{ submitButtonText }}
        </a-button>
      </slot>
    </a-form-item>
  </a-form>

  <slot
    v-if="props.successType === 'result' && submitStatusRef === 'success'"
    name="submitSuccess"
    :data="commandData"
  >
    <a-result
      status="success"
      title="Registro realizado com sucesso"
      sub-title="Agora você pode fechar essa janela"
    >
      <template #extra>
        <a-button @click="() => emit('close')"> Fechar </a-button>
      </template>
    </a-result>
  </slot>
</template>
